// IntradayScan.jsx
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import './IntradayScan.css';
import WatchlistStar from './WatchlistStar';
import RefreshButton from './RefreshButton';
import IntradayBoost from './Intradayboost.js';
import TopGainers from './TopGainers';
import TopLevelStocks from './TopLevelStocks';
import LowLevelStocks from './LowLevelStocks';
import VolumeBoost from './VolumeBoost.js';

const IntradayScan = ({ onBack }) => {
  const [stockData, setStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBoost, setShowBoost] = useState(false);
  const [showGainers, setShowGainers] = useState(false);
  const [showTopLevel, setShowTopLevel] = useState(false);
  const [showLowLevel, setShowLowLevel] = useState(false);
  const [ShowVolume, setShowVolume] = useState(false);

  const fetchTurnoverStocks = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/high-turnover-stocks?top=30`);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${res.status}`);
      }
      const response = await res.json();

      if (response.status !== 'success') {
        throw new Error(response.message || 'Failed to fetch high turnover stocks');
      }

      // Use response.data directly as it contains our stock data
      const stocks = Array.isArray(response.data) ? response.data : [];
      
      const formatted = stocks.map((stock, index) => {
        const symbol = stock?.symbol ?? '—';
        const rawTurnover = Number(stock?.turnover) || 0;
        // Convert turnover to Crores (assuming turnover is in rupees)
        const turnoverCr = rawTurnover / 1e7;

        return {
          rank: index + 1,
          symbol,
          turnover: turnoverCr.toFixed(2),
        };
      });

      if (formatted.length === 0) {
        setError('No high turnover stock data found. Please try again later.');
      } else {
        setError('');
      }
      setStockData(formatted);
    } catch (err) {
      setError(err.message || 'Failed to load data.');
      setStockData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTurnoverStocks();
  }, []);

  if (showBoost) return <IntradayBoost onBack={() => setShowBoost(false)} />;
  if (showGainers) return <TopGainers onBack={() => setShowGainers(false)} />;
  if (showTopLevel) return <TopLevelStocks onBack={() => setShowTopLevel(false)} />;
  if (showLowLevel) return <LowLevelStocks onBack={() => setShowLowLevel(false)} />;
  if (ShowVolume) return <VolumeBoost onBack={() => setShowVolume(false)} />;

  return (
    <div className="intraday-page">
      {/* Header */}
      <header className="intraday-header">
        <div className="intraday-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U" />
          </div>
          <div className="intraday-user">
            <RefreshButton 
              onRefresh={fetchTurnoverStocks} 
              isLoading={loading}
              size="small"
              style={{ marginRight: '10px' }}
            />
            <button onClick={onBack}>← Back to Dashboard</button>
          </div>
        </div>
      </header>

      {/* Page Content */}
      <main className="intraday-main">
        <div className="intraday-top">
          <h1 className="page-title">
            Intraday Scan <span className="info-icon">ℹ️</span>
          </h1>
          <div className="categories">
            <button className="active">High Turnover Stocks</button>
            <button onClick={() => setShowBoost(true)} className="Intraday-buttons">Intraday Surge</button>
            <button onClick={() => setShowVolume(true)} className="Intraday-buttons">Volume Rush</button>
            <button onClick={() => setShowGainers(true)} className="Intraday-buttons">Top Gainers & Losers</button>
            <button onClick={() => setShowLowLevel(true)} className="Intraday-buttons">Day Low Stocks</button>
          </div>
          <div className="filters">
            <span>Filter By:</span>
            <button className="bullish">Bullish</button>
            <button className="bearish">Bearish</button>
          </div>
        </div>

        {/* Table Section */}
        <div className="intraday-table-container">
          {loading ? (
            <div className="loading">⏳ Loading stock data...</div>
          ) : error ? (
            <div className="error-message">⚠️ {error}</div>
          ) : (
            <div className="intraday-table">
              <div className="table-header">
                <div>Rank</div>
                <div>Symbol</div>
                <div>Turnover (₹ Cr)</div>
                <div>SIGNALS</div>
              </div>

              {stockData.map((stock) => {
                const turnoverVal = parseFloat(stock.turnover);
                const isHighPower = turnoverVal > 50;
                return (
                  <div key={`${stock.symbol}-${stock.rank}`} className="table-row">
                    <div>{stock.rank}</div>
                    <div>
                      <WatchlistStar 
                        stock={{
                          symbol: stock.symbol,
                          name: stock.symbol
                        }}
                      />
                      {stock.symbol}
                    </div>
                    <div>{stock.turnover}</div>
                    <div className="signals-cell">
                      <span className="signal-badge" style={{
                        '--arrow-color': isHighPower ? '#10b981' : '#ef4444',
                        color: 'var(--arrow-color)',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        display: 'inline-block',
                        lineHeight: '1'
                      }}>
                        {isHighPower ? '↑' : '↓'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default IntradayScan;
